import { isCitizen } from "modules/auth";
import {
    Button,
    Modal,
    ModalFooter,
    ModalHeader,
    ProfileSkeleton,
    RoleType,
    useTitle,
} from "modules/common";
import AlarmsForm from "modules/common/components/Alarms/Form/AlarmsForm";
import { SaveStationAlarmsRequest } from "modules/common/components/Alarms/Form/utils/schemas/AlarmPayloadSchema";
import { ProfileAlarms } from "modules/common/components/Alarms/ProfileAlarms";
import { IAlarmGroup } from "modules/common/components/Alarms/types/AlarmModels";
import { useModal } from "modules/common/components/Modal/useModal";
import { ProfileAccountDeactivation } from "modules/common/components/ProfileAccountDeactivation/ProfileAccountDeactivation";
import { ProfileUserInformations } from "modules/common/components/ProfileUserInformations/ProfileUserInformations";
import { StationPreferencesSettings } from "modules/common/components/StationPreferencesSettings";
import { DeactivateAccountModal, Settings } from "modules/dashboard";
import { MeasuresEducations } from "modules/dashboard/components/MeasuresEducations";
import { Registries } from "modules/dashboard/components/Registries";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAlarmGroupsMutations, useAlarmsForUser } from "services/iot/alarmsHooks";
import { RootState } from "store";

const isFarmer = (roleType: number | null | undefined) => RoleType.Farmer === roleType;

export const Profile: React.FC = () => {
    useTitle("Profil - eSavjetnik");
    const user = useSelector((state: RootState) => state.signIn?.user);
    const { profile, isLoading, isUpdating } = useSelector((state: RootState) => state.profile);

    const role = user?.role?.value ?? profile?.roleType?.value;
    const shouldFetchAlarms = role !== undefined && !isCitizen(role);

    const { isLoading: isIotLoading, alarmGroups } = useAlarmsForUser(shouldFetchAlarms);
    const alarmGroupMutations = useAlarmGroupsMutations();

    const [showDeactivateModal, setShowDeactivateModal] = useState(false);

    const {
        closeModal: closeAlarmGroupModal,
        openModal: openAlarmGroupModal,
        data: alarmGroupModalData,
        isVisible: isAlarmGroupModalVisible,
    } = useModal<IAlarmGroup>();

    const {
        closeModal: closeDeleteAlarmGroupModal,
        openModal: openDeleteAlarmGroupModal,
        data: deleteAlarmGroupModalData,
        isVisible: isDeleteAlarmGroupModalVisible,
    } = useModal<IAlarmGroup>();

    const roleType = user?.role?.value ?? profile?.roleType?.value;

    const [activatedModules, setActivatedModules] = useState<{ values: number[] }[]>([
        { values: profile?.moduleName ?? [] },
    ]);

    useEffect(() => {
        setActivatedModules([{ values: profile?.moduleName ?? [] }]);
    }, [profile?.moduleName]);

    const toggleDeactivateModal = () => {
        setShowDeactivateModal(!showDeactivateModal);
    };

    const createAlarmGroup = (request: SaveStationAlarmsRequest) => {
        alarmGroupMutations.save.mutate(request);
        closeAlarmGroupModal();
    };

    const updateAlarmGroup = (request: SaveStationAlarmsRequest) => {
        alarmGroupMutations.save.mutate(request);
        closeAlarmGroupModal();
    };

    const deleteAlarmGroup = () => {
        if (!deleteAlarmGroupModalData?.id) {
            return;
        }

        alarmGroupMutations.deleteAlarmGroup.mutate(deleteAlarmGroupModalData);
        closeDeleteAlarmGroupModal();
    };

    return (
        <>
            {isLoading || (shouldFetchAlarms && isIotLoading) || isUpdating ? (
                <ProfileSkeleton />
            ) : (
                <div className="profile-container">
                    <div>
                        <ProfileUserInformations profile={profile} roleType={roleType} />

                        <div className="section-separator" />
                        <Settings
                            profile={profile}
                            modules={activatedModules}
                            roleType={roleType}
                        />

                        <div className="section-separator" />
                        {!isCitizen(role) && (
                            <StationPreferencesSettings enabled={shouldFetchAlarms} />
                        )}

                        {isFarmer(roleType) && <Registries mibpg={profile?.mibpg!} />}

                        {isFarmer(roleType) && (
                            <MeasuresEducations oib={profile?.oib!} county={profile?.county} />
                        )}
                        {!isCitizen(role) && (
                            <ProfileAlarms
                                savedAlarms={alarmGroups}
                                openCreateAlarmGroupModal={openAlarmGroupModal}
                                openUpdateAlarmGroupModal={openAlarmGroupModal}
                                openDeleteAlarmGroupModal={openDeleteAlarmGroupModal}
                            />
                        )}

                        <ProfileAccountDeactivation
                            profile={profile}
                            toggleDeactivateModal={toggleDeactivateModal}
                        />
                    </div>
                </div>
            )}
            {!isCitizen(role) && (
                <div className="alarm-group-modal">
                    <Modal
                        isActive={isAlarmGroupModalVisible}
                        onClose={closeAlarmGroupModal}
                        maintainHeight={90}
                        modalHeader={
                            <ModalHeader
                                onClose={closeAlarmGroupModal}
                                title={
                                    alarmGroupModalData?.id
                                        ? "Ažuriranje grupe alarma"
                                        : "Kreiranje grupe alarma"
                                }
                            />
                        }
                        maintainWidth={50}
                    >
                        <AlarmsForm
                            alarmGroups={alarmGroups}
                            alarmGroupModalData={alarmGroupModalData}
                            closeAlarmGroupModal={closeAlarmGroupModal}
                            createAlarmGroup={createAlarmGroup}
                            updateAlarmGroup={updateAlarmGroup}
                        />
                    </Modal>
                </div>
            )}

            {!isCitizen(role) && (
                <Modal
                    isActive={isDeleteAlarmGroupModalVisible}
                    onClose={closeDeleteAlarmGroupModal}
                    modalHeader={
                        <ModalHeader
                            onClose={closeDeleteAlarmGroupModal}
                            title="Brisanje grupe alarma"
                            isLoading={false}
                        />
                    }
                    modalFooter={
                        <ModalFooter>
                            <Button
                                type="button"
                                size="small"
                                variant="primary-ghost"
                                onClick={closeDeleteAlarmGroupModal}
                            >
                                <span>Odustani</span>
                            </Button>
                            <Button
                                type="button"
                                size="small"
                                variant="warning"
                                onClick={deleteAlarmGroup}
                            >
                                <span>Obriši grupu alarma</span>
                            </Button>
                        </ModalFooter>
                    }
                >
                    <p className="mt-40 mr-40 mb-40 ml-40">
                        Jeste li sigurni da želite obrisati grupu alarma? Ova akcija je nepovratna i
                        svi alarmi u grupi će biti obrisani.
                    </p>
                </Modal>
            )}

            <DeactivateAccountModal
                toggleModal={toggleDeactivateModal}
                isVisible={showDeactivateModal}
                userUuId={profile?.uid ?? ""}
                userRole={profile?.roleType?.value ?? 0}
            />
        </>
    );
};
